import "@/styles/globals.css";
import { useEffect } from "react";
import type { AppProps } from "next/app";
import { QueryClientProvider, QueryClient } from "react-query";
import { SessionProvider } from "next-auth/react";
import PaywallHost from "@/components/PaywallHost";

/**
 * Tabs that stay open across a deploy reference chunk files the new build
 * no longer serves; the next lazy-load then throws ChunkLoadError, which
 * users see as "a client-side exception has occurred". One hard reload
 * fetches the new build and recovers — guarded so a genuinely broken build
 * can't reload-loop.
 */
function useStaleChunkRecovery() {
  useEffect(() => {
    const isChunkError = (msg: unknown) =>
      typeof msg === "string" &&
      /ChunkLoadError|Loading chunk .+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
        msg
      );
    const recover = () => {
      const KEY = "frz-chunk-reload-at";
      const last = Number(sessionStorage.getItem(KEY) ?? 0);
      if (Date.now() - last < 60_000) return; // already tried — don't loop
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    };
    // Uncaught errors land in pod logs so crashes are diagnosable without
    // asking users for console dumps. Throttled to one report per 5s.
    let lastReport = 0;
    const report = (message: string, stack?: string) => {
      if (Date.now() - lastReport < 5000) return;
      lastReport = Date.now();
      try {
        void fetch("/api/client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message,
            stack: stack ?? "",
            url: window.location.href,
            ua: navigator.userAgent,
          }),
        });
      } catch {
        /* reporting must never throw */
      }
    };
    const onError = (e: ErrorEvent) => {
      if (isChunkError(e.message) || isChunkError(String(e.error))) {
        recover();
        return;
      }
      const err = e.error as { stack?: string } | undefined;
      report(String(e.message ?? e.error ?? "unknown error"), err?.stack);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason as { name?: string; message?: string; stack?: string } | undefined;
      if (r?.name === "ChunkLoadError" || isChunkError(r?.message)) {
        recover();
        return;
      }
      report(String(r?.message ?? e.reason ?? "unhandled rejection"), r?.stack);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}

export default function App({
  Component,
  pageProps: { session, ...pageProps },
}: AppProps) {
  const queryClient = new QueryClient();
  useStaleChunkRecovery();
  return (
    <SessionProvider session={session}>
      <QueryClientProvider client={queryClient}>
        <Component {...pageProps} />
        <PaywallHost />
      </QueryClientProvider>
    </SessionProvider>
  );
}
